import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { ProjectBlockedState } from '@/features/projects/components/ProjectBlockedState'
import { epochLabel } from '@/features/projects/format'
import { asApiError } from '@/services/apiError'
import { useAppDispatch, useAppSelector } from '@/store'

import {
  decideDecision,
  decisionsClosed,
  decisionsErrorDismissed,
  decisionsRefetchArmed,
  openDecisions,
  selectDecisionMutation,
  selectDecisionRows,
  selectDecisionsBlocked,
  selectDecisionsError,
  selectDecisionsLoading,
  selectDecisionsNonce,
  supersedeDecision,
} from '../slices/decisionsSlice'
import type {
  DecisionDecideInput,
  DecisionFeatureError,
  DecisionSupersedeInput,
  DecisionView,
} from '../types'
import { DecisionActionForm } from './DecisionActionForm'

const TONE: Record<DecisionView['status'], StatusTone> = {
  OPEN: 'review',
  DECIDED: 'decided',
  SUPERSEDED: 'neutral',
}

function DecisionRow({
  decision,
  orgId,
  projectId,
  error,
}: {
  decision: DecisionView
  orgId: string
  projectId: string
  error: DecisionFeatureError | null
}) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const pending = useAppSelector(selectDecisionMutation(decision.ref))
  const chosen = decision.options.find(
    (option) => option.key === decision.chosenOptionKey
  )
  const actionError = error?.scope === decision.ref ? error : null

  const decide = async (input: DecisionDecideInput): Promise<boolean> => {
    const action = await dispatch(
      decideDecision({ orgId, projectId, decisionRef: decision.ref, input })
    )
    return decideDecision.fulfilled.match(action)
  }

  const supersede = async (input: DecisionSupersedeInput): Promise<boolean> => {
    const action = await dispatch(
      supersedeDecision({ orgId, projectId, decisionRef: decision.ref, input })
    )
    return supersedeDecision.fulfilled.match(action)
  }

  return (
    <article
      aria-labelledby={`decision-title-${decision.ref}`}
      className={
        decision.overdue
          ? 'rounded-lg border-2 border-review bg-review-bg px-4 py-4'
          : 'rounded-lg border border-border bg-card px-4 py-4'
      }
      data-testid={`decision-row-${decision.ref}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-semibold text-foreground">
              {decision.ref}
            </span>
            <StatusPill tone={TONE[decision.status]}>
              {t(`blueprint.decisions.status.${decision.status}`)}
            </StatusPill>
          </div>
          <h3
            id={`decision-title-${decision.ref}`}
            className="mt-1 font-serif text-lg font-semibold text-foreground"
          >
            {decision.title}
          </h3>
          <p className="mt-1 max-w-3xl text-sm text-ink-soft">
            {decision.context}
          </p>
        </div>
        <div className="shrink-0 text-left sm:text-right">
          <p className="v-figure text-sm font-semibold tabular-nums text-foreground">
            {t('blueprint.decisions.ageDays', { count: decision.ageDays })}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('blueprint.decisions.dueAt')}: {epochLabel(decision.dueAt)}
          </p>
          {decision.overdue && (
            <p className="mt-1 text-xs font-medium text-review">
              {t('blueprint.decisions.overdue')}
            </p>
          )}
        </div>
      </div>

      <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium text-muted-foreground">
            {t('blueprint.decisions.owner')}
          </dt>
          <dd className="mt-0.5 text-foreground">
            {decision.ownerId}
            {decision.ownerSide ? ` · ${decision.ownerSide}` : ''}
          </dd>
        </div>
        {decision.status !== 'OPEN' && (
          <div>
            <dt className="text-xs font-medium text-muted-foreground">
              {t('blueprint.decisions.decidedBy')}
            </dt>
            <dd className="mt-0.5 text-foreground">
              {decision.decidedBy ?? '—'} · {epochLabel(decision.decidedAt)}
            </dd>
          </div>
        )}
      </dl>

      {decision.options.length > 0 && (
        <ul className="mt-3 border-t border-border pt-2 text-sm">
          {decision.options.map((option) => (
            <li key={option.key} className="v-row py-1.5">
              <span className="font-medium text-foreground">
                {option.label}
              </span>
              {option.effortDeltaHours !== undefined && (
                <span className="ml-2 text-muted-foreground">
                  {t('effort.hours', { hours: option.effortDeltaHours })}
                </span>
              )}
              {option.rationale !== undefined && (
                <span className="ml-2 text-ink-soft">{option.rationale}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {chosen !== undefined && (
        <p className="mt-3 text-sm text-foreground">
          {t('blueprint.decisions.chosenOption')}: {chosen.label}
        </p>
      )}
      {decision.decisionRationale !== undefined && (
        <p className="mt-1 text-sm text-ink-soft">
          {decision.decisionRationale}
        </p>
      )}
      {decision.supersededByDecisionId !== undefined && (
        <p className="mt-1 text-sm text-ink-soft">
          {t('blueprint.decisions.supersededBy')}:{' '}
          {decision.supersededByDecisionId}
        </p>
      )}
      {decision.blocks.length > 0 && (
        <p className="mt-3 text-sm text-review">
          {t('blueprint.decisions.blocks')}:{' '}
          {decision.blocks
            .map((block) => `${block.refType} ${block.ref}`)
            .join(', ')}
        </p>
      )}

      <DecisionActionForm
        decision={decision}
        pending={pending}
        error={actionError}
        onDecide={decide}
        onSupersede={supersede}
        onDismissError={() => dispatch(decisionsErrorDismissed(decision.ref))}
      />
    </article>
  )
}

/** The Blueprint's live, project-keyed decisions spine. */
export function DecisionsPanel({
  orgId,
  projectId,
}: {
  orgId: string
  projectId: string
}) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const rows = useAppSelector(selectDecisionRows)
  const loading = useAppSelector(selectDecisionsLoading)
  const error = useAppSelector(selectDecisionsError)
  const blocked = useAppSelector(selectDecisionsBlocked)
  const nonce = useAppSelector(selectDecisionsNonce)
  const listError = error !== null && error.scope === undefined ? error : null

  useEffect(() => {
    return () => {
      dispatch(decisionsClosed())
    }
  }, [dispatch, orgId, projectId])

  useEffect(() => {
    void dispatch(openDecisions({ orgId, projectId }))
  }, [dispatch, orgId, projectId, nonce])

  const retry = (): void => {
    dispatch(decisionsErrorDismissed())
    dispatch(decisionsRefetchArmed())
    void dispatch(openDecisions({ orgId, projectId }))
  }

  if (blocked !== null) return <ProjectBlockedState blocked={blocked} />

  return (
    <section aria-labelledby="blueprint-decisions-heading" className="mt-7">
      <SectionLabel>{t('blueprint.decisions.label')}</SectionLabel>
      <h2
        id="blueprint-decisions-heading"
        className="mt-0.5 font-serif text-lg font-semibold text-foreground"
      >
        {t('blueprint.decisions.title')}
      </h2>
      <p className="mt-1 max-w-3xl text-sm text-ink-soft">
        {t('blueprint.decisions.lede')}
      </p>
      <div className="mt-3">
        {/* Only a first load shows the loading panel: a background refetch
            keeps every row, and its typed rationale, mounted. */}
        {loading && rows.length === 0 ? (
          <LoadingPanel label={t('blueprint.decisions.loading')} />
        ) : listError !== null ? (
          <ErrorPanel
            error={asApiError({
              status: listError.status,
              key: listError.code,
              message: listError.message,
            })}
            onRetryClicked={retry}
          />
        ) : rows.length === 0 ? (
          <EmptyPanel
            title={t('blueprint.decisions.empty.title')}
            body={t('blueprint.decisions.empty.body')}
          />
        ) : (
          <div className="space-y-3">
            {rows.map((decision) => (
              <DecisionRow
                key={decision.ref}
                decision={decision}
                orgId={orgId}
                projectId={projectId}
                error={error}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
